import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Typography,
  Box,
  Divider,
} from "@mui/material";

const MotivationPanel = ({ callReasons = [], open, onClose, saveReasons, selectedReasons, setSelectedReasons, currentCallId, setLastCallId, lastCallId, loadReasons }) => {
  let selectedSnapshot = selectedReasons ? selectedReasons.slice() : [];

  // Reset selected reasons when modal opens for a different call
  useEffect(async () => {
    if (open && currentCallId && currentCallId !== lastCallId) {
      if (loadReasons) {
        const loadedReasons = await loadReasons();
        selectedSnapshot = loadedReasons;
        setSelectedReasons(loadedReasons);
      } else {
        setSelectedReasons([]);
      }
      setLastCallId(currentCallId);
    }
  }, [open, currentCallId, lastCallId, setSelectedReasons]);

  const handleReasonToggle = (reasonId) => {
    setSelectedReasons((prevSelected) => {
      if (prevSelected.includes(reasonId)) {
        // Remove if already selected
        return prevSelected.filter((id) => id !== reasonId);
      } else {
        // Add if not selected
        return [...prevSelected, reasonId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedReasons.length === callReasons.length) {
      // If all are selected, deselect all
      setSelectedReasons([]);
    } else {
      // Select all
      setSelectedReasons(callReasons.map((reason) => reason.id));
    }
  };

  const handleSave = () => {
    const selectedReasonObjects = callReasons.filter((reason) =>
      selectedReasons.includes(reason.id)
    );
    saveReasons(selectedReasonObjects);
    onClose();
  };

  const handleCancel = () => {
    setSelectedReasons(selectedSnapshot);
    onClose();
  };

  const isAllSelected = selectedReasons.length === callReasons.length && callReasons.length > 0;
  const isIndeterminate = selectedReasons.length > 0 && selectedReasons.length < callReasons.length;

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div">
          Selecionar Motivações
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Escolha uma ou mais motivações para esta ligação
        </Typography>
      </DialogTitle>
      
      <DialogContent dividers>
        {callReasons.length > 0 ? (
          <Box>
            {/* Select All Option */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onChange={handleSelectAll}
                  color="primary"
                />
              }
              label={
                <Typography variant="subtitle2" fontWeight="bold">
                  Selecionar Todas
                </Typography>
              }
            />
            <Divider sx={{ my: 2 }} />
            
            {/* Individual Reasons */}
            <FormGroup>
              {callReasons.map((reason) => (
                <FormControlLabel
                  key={reason.id}
                  control={
                    <Checkbox
                      checked={selectedReasons.includes(reason.id)}
                      onChange={() => handleReasonToggle(reason.id)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {reason.name}
                      </Typography>
                      {reason.description && reason.description !== reason.name && (
                        <Typography variant="body2" color="text.secondary">
                          {reason.description}
                        </Typography>
                      )}
                      {reason.success_indicator === 1 && (
                        <Typography variant="caption" color="success.main" sx={{ display: 'block' }}>
                          Indicador de Sucesso
                        </Typography>
                      )}
                    </Box>
                  }
                  sx={{
                    mb: 1,
                    ml: 0,
                    alignItems: 'flex-start',
                    '& .MuiFormControlLabel-label': {
                      pt: 0.5,
                    },
                  }}
                />
              ))}
            </FormGroup>
          </Box>
        ) : (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              Nenhuma motivação disponível
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleCancel} variant="outlined">
          Cancelar
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={selectedReasons.length === 0}
        >
          Salvar ({selectedReasons.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MotivationPanel;
